import React, { useState, useMemo, useContext } from 'react'
import Slider from '@mui/material/Slider'
import { Grant } from '../../../../generated/graphql'
import { KTSVG } from '../../../../_metronic/helpers'
import { GrantsRequiringActionDrawer } from './GrantsRequiringActionDrawer'
import { GrantCalculatorDrawer } from './GrantCalculatorDrawer'
import {
  formatNumber,
  getCurrency,
  getSymbolOfCurrency,
} from '../../../../utils/CurrencyFormatter'
import { GlobalContext } from '../../store/context/globalContext'

type Props = {
  grants: Grant[]
  selectedRange?: number
  onExpiryRangeChange?: (range: number) => void
  onAddFMVClick?: () => void
  selectedRangeLabel?: string
  companyCardRef?: React.RefObject<{ scrollIntoView: (options?: ScrollIntoViewOptions) => void }>
}

const formatDate = (date: string | Date) => {
  if (!date) return ''
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const month = monthNames[d.getMonth()]
  const year = d.getFullYear()
  return `${day} ${month} ${year}`
}

const RANGE_MARKS = [
  { value: 1, label: '1 mo' },
  { value: 3, label: '3 mo' },
  { value: 6, label: '6 mo' },
  { value: 12, label: '1 yr' },
  { value: 24, label: '2 yr' },
  { value: 37, label: '3+ yr' },
]

const getSelectedRangeLabel = (m: number) => {
  if (m === 1) return '1 month'
  if (m === 3) return '3 months'
  if (m === 6) return '6 months'
  if (m === 12) return '1 year'
  if (m === 24) return '2 years'
  if (m === 37) return '3+ years'
  return `${m} months`
}

const ExpiryAnalysisCard: React.FC<Props> = ({
  grants,
  selectedRange = 3,
  onExpiryRangeChange,
  onAddFMVClick,
  selectedRangeLabel,
  companyCardRef,
}) => {
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)
  
  const [range, setRange] = useState<number>(selectedRange)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [calculatorDrawerOpen, setCalculatorDrawerOpen] = useState(false)

  const now = new Date()

  const futureDate = new Date(now)
  if (range === 37) {
    futureDate.setFullYear(now.getFullYear() + 100)
  } else {
    futureDate.setMonth(now.getMonth() + range)
  }

  const upcomingGrants = useMemo(() => {
    return grants.filter((grant) => {
      if (!grant.expiryDate) return false
      if (grant.vestedOptions! <= 0 || grant.vestedOptions! - grant.numberExercised! <= 0)
        return false
      if (grant.status !== 'InActive') return false // Only includes InActive grants
      const expiry = new Date(grant.expiryDate)
      if (range === 37) {
        return expiry >= now
      }
      return expiry >= now && expiry <= futureDate
    })
  }, [grants, range])

  // const calculatePotentialLoss = useMemo(() => {
  //   const expiringGrants = upcomingGrants.filter(
  //     (g) =>
  //       g.companyFMV &&
  //       g.companyFMV > 0 &&
  //       g.numberGranted > 0 &&
  //       g.status === 'InActive' &&
  //       g.vestedOptions &&
  //       g.vestedOptions > 0
  //   )
  //   const expiringValue = expiringGrants.reduce((acc, g) => {
  //     const exercised = g.numberExercised || 0
  //     return acc + g.companyFMV! * (g.vestedOptions! - exercised)
  //   }, 0)
  //   const potentialLoss =
  //     expiringValue -
  //     expiringGrants.reduce((acc, g) => {
  //       const exercised = g.numberExercised || 0
  //       return acc + (g.exercisePriceOrSarValue! || 0) * (g.vestedOptions! - exercised)
  //     }, 0)
  //   return {
  //     expiringValue,
  //     potentialLoss,
  //   }
  // }, [upcomingGrants])

    const calculatePotentialLoss = useMemo(() => {
    const expiringGrants = upcomingGrants.filter(
      (g) =>
        g.companyFmvinUserCurrency &&
      (Number(g.companyFmvinUserCurrency)) > 0 &&
        g.numberGranted > 0 &&
        g.status === 'InActive' &&
        g.vestedOptions &&
        g.vestedOptions > 0
    )
    const expiringValue = expiringGrants.reduce((acc, g) => {
      const exercised = g.numberExercised || 0
      return acc + Number(g.companyFmvinUserCurrency!) * (g.vestedOptions! - exercised)
    }, 0)
    const potentialLoss =
      expiringValue -
      expiringGrants.reduce((acc, g) => {
        const exercised = g.numberExercised || 0
        // return acc + (g.exercisePriceOrSarValue! || 0) * (g.vestedOptions! - exercised)
        return acc + Number(g.strikePriceinUserCurrency! || 0) * (g.vestedOptions! - exercised)
      }, 0)
    return {
      expiringValue,
      potentialLoss,
    }
  }, [upcomingGrants])

  const nextExpiryGrant =
    upcomingGrants.length > 0
      ? upcomingGrants.sort(
          (a, b) => new Date(a.expiryDate!).getTime() - new Date(b.expiryDate!).getTime()
        )[0]
      : null

  const fmvAllMissing = grants.length > 0 && grants.every((g) => !g.companyFmvinUserCurrency)
  const fmvPartial = !fmvAllMissing && grants.some((g) => !g.companyFmvinUserCurrency)

  const snapToMark = (v: number) => {
    let nearest = RANGE_MARKS[0].value
    let dmin = Math.abs(v - nearest)
    for (const m of RANGE_MARKS) {
      const d = Math.abs(v - m.value)
      if (d < dmin) {
        dmin = d
        nearest = m.value
      }
    }
    return nearest
  }

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const raw = Array.isArray(newValue) ? newValue[0] : newValue
    const snapped = snapToMark(raw)
    setRange(snapped)
    if (snapped !== range) onExpiryRangeChange?.(snapped)
  }

  const handleAddFMVClick = () => {
    if (companyCardRef?.current) {
      companyCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    onAddFMVClick?.()
  }

  const handleCalculateExerciseCost = () => {
    if (nextExpiryGrant) {
      setCalculatorDrawerOpen(true)
    }
  }

  const formatCurrency = (value: number): string => {
    return `${getSymbolOfCurrency(userCurrency)}${formatNumber({
      value: Number(value),
      format: userCurrencyFormat,
    })}`
  }

  return (
    <div className='card border border-gray-300 p-6 p-md-8 mb-5 mb-md-0'>
      <div className='card-header px-0'>
        <h3 className='card-title flex-column'>
          <span className='card-label gap-2 mb-2 fs-4 fs-md-3'>Expiry Risk & Opportunity</span>
          <small className='text-gray-700 fs-7 fs-md-6'>
            Monitor upcoming expiries and potential value at risk
          </small>
        </h3>
      </div>
      <div className='card-body px-0 py-4 py-md-6'>
        {upcomingGrants.length > 0 && (
          <div className='border border-warning p-4 p-md-6 rounded mb-5 mb-md-0 d-flex flex-column gap-3'>
            <div className='d-flex align-items-center gap-2'>
              <img
                src='/media/custom-images/warning.svg'
                alt='Warning Icon'
                className='svg-icon-2 svg-icon-md-1 mt-1'
              />
              <div className='fw-bold text-warning fs-6 fs-md-5'>Action Required</div>
            </div>
            <div className='text-dark fs-7 fs-md-6'>
              You have{' '}
              <strong>
                {upcomingGrants.length} grant{upcomingGrants.length > 1 ? 's' : ''}
              </strong>{' '}
              expiring in the next <strong>{getSelectedRangeLabel(range)}</strong> worth{' '}
              <strong>{formatCurrency(calculatePotentialLoss.expiringValue)}</strong>
            </div>

            <div className='d-flex flex-column flex-sm-row align-items-start gap-2 gap-md-3'>
              {upcomingGrants.length === 1 && (
                <button
                  className='btn btn text-white btn-sm p-2 px-3 px-md-4 rounded'
                  style={{ backgroundColor: '#f59f0a' }}
                  onClick={handleCalculateExerciseCost}
                >
                  <KTSVG
                    path='/media/icons/duotune/finance/fin007.svg'
                    className='svg-icon-2 text-white'
                  />
                  Calculate Exercise Cost
                </button>
              )}
              <button
                style={{ color: '#f59f0a' }}
                className='btn border border-warning btn-sm p-2 px-3 px-md-4 rounded'
                onClick={() => setDrawerOpen(true)}
              >
                See Details
              </button>
              <GrantsRequiringActionDrawer
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                grants={upcomingGrants}
                expiringWindow={getSelectedRangeLabel(range)}
              />
              {nextExpiryGrant && (
                <GrantCalculatorDrawer
                  isOpen={calculatorDrawerOpen}
                  onClose={() => setCalculatorDrawerOpen(false)}
                  grant={nextExpiryGrant}
                />
              )}
            </div>
          </div>
        )}
        <div className='row g-4 g-md-5 mt-3'>
          {[
            {
              label: 'Next Expiry',
              value: nextExpiryGrant ? formatDate(nextExpiryGrant.expiryDate) : '—',
              subtext: nextExpiryGrant ? nextExpiryGrant.companyName : '—',
              iconPath: '/media/custom-images/nextExpiry.svg',
              badgeClass: 'success',
            },
            {
              label: 'Expiring Window',
              value: getSelectedRangeLabel(range),
              subtext:
                upcomingGrants.length > 0
                  ? `${upcomingGrants.length} grant${upcomingGrants.length > 1 ? 's' : ''} at risk`
                  : '—',
              iconPath: '/media/custom-images/ExpiryWindow.svg',
              badgeClass: 'warning',
            },
            {
              label: 'Expiring Value',
              value: formatCurrency(calculatePotentialLoss.expiringValue),
              subtext: 'Potential gain',
              iconPath: '/media/custom-images/success.svg',
              badgeClass: 'success',
            },
            {
              label: 'Potential Loss',
              value: formatCurrency(calculatePotentialLoss.potentialLoss),
              subtext: 'If not exercised',
              iconPath: '/media/custom-images/potentialLoss.svg',
              badgeClass: 'danger',
            },
          ].map((metric, i) => (
            <div className='col-12 col-md-4 col-lg-3 mb-5 mb-md-0' key={i}>
              <div className='card border border-gray-300 h-100 shadow-md hover:shadow-lg'>
                <div className='card-body d-flex align-items-center gap-2 gap-md-3 p-4 p-md-5'>
                  {metric.iconPath && (
                    <div
                      className={`d-flex align-items-center justify-content-center flex-shrink-0 rounded bg-light-${metric.badgeClass}`}
                      style={{ width: '30px', height: '30px' }}
                    >
                      <KTSVG
                        path={metric.iconPath}
                        className={`svg-icon-2 svg-icon-md-1 text-${
                          metric.badgeClass || 'gray-600'
                        }`}
                      />
                    </div>
                  )}
                  <div className='flex-grow-1 min-width-0'>
                    <div className='d-flex align-items-start justify-content-between'>
                      <div className='fw-bold fs-7 fs-md-6 lh-sm'>{metric.label}</div>
                      {i >= 2 && fmvPartial && (
                        <button
                          type='button'
                          onClick={handleAddFMVClick}
                          className='btn btn-link p-0 fs-8 fs-md-7 text-warning flex-shrink-0 lh-1'
                          style={{ marginTop: '2px' }}
                        >
                          Fill FMV
                        </button>
                      )}
                    </div>
                    <div className='fs-2 fs-md-1 fw-bold text-dark'>{metric.value}</div>
                    <div className='fs-8 fs-md-7 text-gray-700'>
                      <span className='fw-normal'>{metric.subtext}</span>
                    </div>
                    {i >= 2 && fmvPartial && (
                      <div className='text-gray-600 fs-8 fs-md-7 mt-1'>
                        ⚠️ Based on partial data
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className='position-relative mt-5 mt-md-6'>
          <div
            className='p-4 p-md-6 mb-5 mb-md-0 rounded card border border-gray-300'
            style={{
              background: fmvAllMissing ? 'rgba(255, 255, 255, 0.65)' : '#fff',
              backdropFilter: fmvAllMissing ? 'blur(10px)' : undefined,
              WebkitBackdropFilter: fmvAllMissing ? 'blur(10px)' : undefined,
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <div className='card-label fw-bold fs-4 fs-md-3'>Risk Timeline Analysis</div>
            <p className='text-gray-700 fs-8 fs-md-7'>Adjust timeline to see value at risk</p>
            <div className='w-95 ms-2 ms-md-5 me-2 me-md-5'>
              <Slider
                value={range}
                onChange={handleSliderChange}
                min={RANGE_MARKS[0].value}
                max={RANGE_MARKS[RANGE_MARKS.length - 1].value}
                step={null}
                marks={RANGE_MARKS}
                valueLabelDisplay='off'
                sx={{
                  height: 4,
                  mt: 2,
                  mb: 2,
                  color: '#ff6404ff',
                  '& .MuiSlider-thumb': {
                    height: 10,
                    width: 10,
                    backgroundColor: '#fff',
                    border: '2px solid #ff6404ff',
                  },
                  '& .MuiSlider-track': {
                    height: 6,
                    borderRadius: 2,
                  },
                  '& .MuiSlider-rail': {
                    height: 6,
                    opacity: 0.3,
                    backgroundColor: '#ddd',
                  },
                  '& .MuiSlider-markLabel': {
                    fontSize: '12px',
                    fontFamily: 'Inter,Helvetica, sans-serif',
                    maxWidth: 40,
                    color: '#757984',
                    transform: 'translateX(-50%) translateY(2px)',
                  },
                }}
              />
            </div>
            <hr />
            <div className='bg-light rounded-lg p-3 p-md-4 text-center'>
              <div className='fw-semibold fs-7 fs-md-6'>Risk Analysis Summary</div>
              <div className='fs-3 fs-md-2 fw-bold mt-1'>
                You could lose{' '}
                <span className='text-danger'>
                  {formatCurrency(calculatePotentialLoss.potentialLoss)}
                </span>{' '}
                if no action is taken
              </div>
              <div className='text-gray-700 fs-8 fs-md-7 mt-1'>
                Exercise cost:{' '}
                {formatCurrency(
                  calculatePotentialLoss.expiringValue - calculatePotentialLoss.potentialLoss
                )}{' '}
                • Potential Value: {formatCurrency(calculatePotentialLoss.expiringValue)}
              </div>
            </div>
          </div>
          {fmvPartial && (
            <div
              className='position-absolute top-0 start-0 w-100 h-100 rounded d-flex align-items-center justify-content-center text-center px-3 px-md-4 card border border-gray-300'
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.37)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                zIndex: 10,
              }}
            >
              <div className='d-flex flex-column align-items-center'>
                <KTSVG
                  path='/media/icons/duotune/general/gen044.svg'
                  className='svg-icon-2x svg-icon-md-3x svg-icon-warning mb-3 mb-md-4'
                  aria-label='FMV warning'
                />
                <div className='text fs-4 fs-md-3 fw-bold mb-2' style={{ color: '#F5A10A' }}>
                  FMV Required for Risk Analysis
                </div>
                <div className='text-gray-700 fs-7 fs-md-6 mb-3 mb-md-4'>
                  Please add FMV data to your grants to see detailed risk timeline analysis
                </div>
                <button
                  className='btn btn-sm border border-warning text fw-bold fs-7 fs-md-6'
                  style={{ color: '#F5A10A' }}
                  onClick={handleAddFMVClick}
                >
                  Add Missing FMV
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ExpiryAnalysisCard